import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Star, Clock3, Sparkles, Hammer, Sofa, Drill,
  Home as HomeIcon, ArrowRight, CheckCircle2, Lock, BadgeCheck, MapPin, Phone, AlertCircle,
  Quote,
} from "lucide-react";
import { BRAND } from "@/lib/brand";
import useSeo from "@/lib/useSeo";
import api from "@/lib/api";
import ThumbtackBadge from "@/components/ThumbtackBadge";
import LocalTrust from "@/components/LocalTrust";
import { PROVIDER_LABEL, IS_QUICKBOOKS } from "@/lib/payments";

const heroImage = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const services = [
  { icon: Sparkles, title: "Standard & Deep Residential House Cleaning", desc: "Routine refreshes and top-to-bottom deep cleans — premium house cleaning in Evansville with eco-friendly products." },
  { icon: Hammer,   title: "Professional Handyman & Assembly Services", desc: "Trusted local handyman services for repairs, mounting, and full furniture assembly — no plumbing or electrical." },
  { icon: Sofa,     title: "Furniture Assembly",  desc: "Flat-pack, custom builds, and reconfigurations handled cleanly and quickly." },
  { icon: Drill,    title: "Minor Repairs",       desc: "Drywall patches, hardware swaps, door tuning, trim, and paint touch-ups." },
  { icon: HomeIcon, title: "Fixture Installation",desc: "Shelving, mirrors, blinds, towel bars, and ceiling fan swaps (non-electrical)." },
  { icon: ShieldCheck, title: "Evansville Property Maintenance Solutions", desc: "Preventive home upkeep, seasonal punch lists, and pressure-washing across the Tri-State Area." },
];

const promises = [
  { icon: Star,     title: "Perfect 5-Star Reputation", desc: "Every job earns a review. Every review fuels the next visit." },
  { icon: Clock3,   title: "Punctual, Every Time",       desc: "Arrival windows we actually keep — your day stays on schedule." },
  { icon: ShieldCheck, title: "Done Right The First Time", desc: "If it's not right, we don't leave. Total peace of mind." },
];

const testimonialsFallback = [
  {
    name: "Angie M.",
    source: "Google",
    tag: "Deep Cleaning",
    featured: true,
    text: "Truly top notch! Deep cleaned my living room including shampooing the carpet. Removed trash, cleaned and polished the furniture… my living room looks and smells amazing!! Thanks for a wonderful job!!!",
  },
  {
    name: "Gary R.",
    source: "Thumbtack",
    tag: "Handyman & Installation",
    text: "Collin and Jay were quick to respond and they arrived at the job before the time they said they were going to arrive meaning they were on time and ready to go. They do things right they don't cut corners. They explain everything to you. They came out, hung two ceiling fans and put two storm doors on… Their integrity is impeccable.",
  },
  {
    name: "Angela O.",
    source: "Thumbtack",
    tag: "Recurring Cleaning & Yard Care",
    text: "A great company! I use their cleaning service weekly. I have had them help with junk removal, removing bushes and trees, and improving my lawn and garden. They are very professional and do excellent work. I highly recommend them!",
  },
  {
    name: "Operations R.",
    source: "Thumbtack",
    tag: "Residential Cleaning",
    text: "Booking a cleaning with them was a very smooth process. They were very responsive and we were very satisfied with the overall service. I would definitely recommend their cleaning services.",
  },
  {
    name: "Taylor W.",
    source: "Thumbtack",
    tag: "Property Improvement",
    text: "Good quality work. And they were very easy to work with. Professional and respectful. I will work with them in the future.",
  },
];

export default function HomePage() {
  useSeo({
    title: "Top-Notch Pro Solutions | Handyman & Home Cleaning Evansville, IN",
    description: "Premium home care and property improvement in Evansville, IN. Offering high-quality handyman services, furniture assembly, and deep or standard residential cleaning. 5-star reliability within a 20-mile radius. Fully insured team.",
  });

  const [testimonials, setTestimonials] = useState(testimonialsFallback);
  useEffect(() => {
    api.get("/testimonials")
      .then((res) => { if (res.data && res.data.length > 0) setTestimonials(res.data); })
      .catch(() => { /* keep fallback */ });
  }, []);

  return (
    <div data-testid="home-page">

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Home interior cleaned by Top-Notch Pro Solutions in Evansville, IN" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28 md:py-40">
          <div className="max-w-3xl">
            <div className="reveal mb-5">
              <ThumbtackBadge variant="hero" />
            </div>
            <div className="reveal flex items-center gap-2 text-[var(--brand-gold)] mb-5">
              <span className="gold-divider" />
              <span className="section-eyebrow">Serving Evansville & Surrounding Areas</span>
            </div>
            <h1 className="reveal reveal-delay-1 text-white font-extrabold leading-[1.05] tracking-tight" style={{ fontFamily: "Outfit", fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
              House Cleaning & Handyman Services in{" "}
              <span className="text-[var(--brand-gold)]">Evansville, IN</span>
            </h1>
            <p className="reveal reveal-delay-2 mt-6 text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
              Local, family-run home services for Evansville and the surrounding Tri-State — book a house cleaning online in minutes, or request a quote for handyman projects like flooring, window installations, painting, and fence repairs.
            </p>

            {/* Dual CTA cards */}
            <div className="reveal reveal-delay-3 mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              <Link
                to="/quote"
                className="group relative rounded-2xl bg-[var(--brand-gold)] hover:bg-[var(--brand-gold-deep)] text-[var(--brand-teal-deep)] p-5 shadow-xl shadow-black/20 transition transform hover:-translate-y-0.5"
                data-testid="hero-cta-book-cleaning"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--brand-teal)] text-white flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-extrabold text-lg leading-tight" style={{ fontFamily: "Outfit" }}>
                      Book Cleaning Online
                    </div>
                    <div className="text-[13px] mt-1 opacity-90 leading-snug">
                      House cleaning — within a 20-mile radius of Evansville
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 mt-1 group-hover:translate-x-1 transition" />
                </div>
              </Link>

              <Link
                to="/request-quote"
                className="group relative rounded-2xl bg-white/95 hover:bg-white text-[var(--brand-teal-deep)] p-5 shadow-xl shadow-black/20 transition transform hover:-translate-y-0.5"
                data-testid="hero-cta-handyman-quote"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--brand-teal)] text-white flex items-center justify-center flex-shrink-0">
                    <Hammer className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-extrabold text-lg leading-tight" style={{ fontFamily: "Outfit" }}>
                      Get a Handyman Quote
                    </div>
                    <div className="text-[13px] mt-1 opacity-90 leading-snug">
                      Flooring, windows, painting, fence repairs & more
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 mt-1 group-hover:translate-x-1 transition" />
                </div>
              </Link>
            </div>

            <div className="reveal reveal-delay-3 mt-4 flex items-center gap-3 text-white/70 text-sm">
              <Phone className="w-4 h-4 text-[var(--brand-gold)]" />
              <span>Prefer to talk it through? Call us at{" "}
                <a href={BRAND.phoneHref} className="text-[var(--brand-gold)] font-semibold hover:text-white" data-testid="hero-cta-call">{BRAND.phone}</a>
              </span>
            </div>

            <div className="reveal reveal-delay-4 mt-8 flex flex-wrap items-center gap-5 text-white/80 text-sm">
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[var(--brand-gold)] fill-[var(--brand-gold)]" /> 5-Star Rated</span>
              <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-[var(--brand-gold)]" /> 256-Bit Secure Booking</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--brand-gold)]" /> Licensed & Insured</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============== EXCLUSIONS BANNER ============== */}
      <div className="bg-[var(--brand-gold)]/15 border-y border-[var(--brand-gold)]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center justify-center gap-3 text-center text-sm font-semibold text-[var(--brand-teal-deep)] flex-wrap">
          <Sparkles className="w-4 h-4 text-[var(--brand-teal)]" />
          <span>
            <strong>Instant Online Booking</strong> — the streamlined way to schedule cleaning within 20 miles of Evansville. For handyman work or cleanings beyond that, we'd love to provide a{" "}
            <Link to="/request-quote" className="underline text-[var(--brand-teal)] font-bold">personalized quote</Link>.
          </span>
        </div>
      </div>

      {/* ============== PROMISES ============== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-eyebrow">Why Homeowners Choose Us</span>
          <h2 className="heading-display mt-3 text-4xl md:text-5xl">Total Peace of Mind — Built In.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {promises.map((p, i) => (
            <div key={p.title} className="card-elevated p-8 reveal" style={{ animationDelay: `${0.08 * i}s` }} data-testid={`promise-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-[var(--brand-teal)]/10 text-[var(--brand-teal)] flex items-center justify-center mb-5">
                <p.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>{p.title}</h3>
              <p className="text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============== SERVICES ============== */}
      <section className="bg-white py-20 md:py-24 border-y border-[#e6e0d4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <span className="section-eyebrow">Our Trade Focus</span>
              <h2 className="heading-display mt-3 text-4xl md:text-5xl">Premium Home Care & Improvement Solutions</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We focus on what we do best — <strong>Evansville property maintenance solutions</strong>, expert handyman work,
                <strong> furniture assembly and minor home repairs</strong>, and premium residential cleaning. We never overpromise.
              </p>
            </div>
            <Link to="/services" className="btn-outline self-start md:self-end" data-testid="services-view-all">
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <article key={s.title} className="group card-elevated p-7 flex flex-col" data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").slice(0,40)}`}>
                <div className="w-12 h-12 rounded-xl bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-[var(--brand-teal)] mb-2" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="section-eyebrow">Booking Made Effortless</span>
            <h2 className="heading-display mt-3 text-4xl md:text-5xl">3 Steps. <br /> Zero Surprises.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Our online quote engine prices your job instantly using transparent matrices. Lock your slot with a $50 deposit and we'll handle the rest.
            </p>
            <ol className="mt-8 space-y-5">
              {[
                ["Build Your Quote", "Pick Standard or Deep clean, your bedrooms/baths, frequency, and add-ons. Watch the price update live."],
                ["Secure Your Slot", IS_QUICKBOOKS
                  ? "Pay a flat $50 deposit via secure QuickBooks Payments — all major cards supported."
                  : "Pay a flat $50 deposit via secure Stripe checkout — cards, Apple Pay, Google Pay all supported."],
                ["We Show Up & Wow You", "Our pros arrive on time, do the job right, and only then is your remaining balance processed."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--brand-gold)] text-white font-bold flex items-center justify-center" style={{ fontFamily: "Outfit" }}>{i + 1}</span>
                  <div>
                    <div className="font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>{t}</div>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/quote" className="btn-primary mt-8" data-testid="howitworks-cta">
              Start Your Instant Booking <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Cleaning service" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 card-elevated p-5 max-w-[240px] hidden md:block">
              <div className="flex items-center gap-1 text-[var(--brand-gold)] mb-2">
                {["s1", "s2", "s3", "s4", "s5"].map((id) => <Star key={id} className="w-4 h-4 fill-[var(--brand-gold)]" />)}
              </div>
              <p className="text-sm text-slate-700 italic">"They treat your home like their own."</p>
              <p className="text-xs text-slate-500 mt-1">— Linda K., Evansville</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== LOCAL TRUST ============== */}
      <LocalTrust variant="home" />

      {/* ============== TESTIMONIALS ============== */}
      <section className="bg-[var(--brand-cream)] py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[var(--brand-gold)]/40 mb-4">
              <Star className="w-3.5 h-3.5 text-[var(--brand-gold)] fill-[var(--brand-gold)]" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--brand-teal)]">What Our Clients Say</span>
            </div>
            <h2 className="heading-display text-4xl md:text-5xl">5-Star Service From Neighbors You Trust</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We are proud to hold a flawless <strong>5.0 rating on Thumbtack</strong>. Here is why Evansville homeowners
              choose us for elite handyman and cleaning services.
            </p>
          </div>

          {/* Featured testimonial (Angie M from Google) */}
          {(() => {
            const featured = testimonials.find((t) => t.featured);
            if (!featured) return null;
            return (
              <article
                className="relative max-w-4xl mx-auto card-elevated p-8 md:p-12 mb-8 overflow-hidden"
                data-testid={`testimonial-${featured.name.replace(/\W+/g, "-")}`}
                style={{ borderTop: "4px solid var(--brand-gold)" }}
              >
                <Quote className="absolute -top-2 -right-2 w-32 h-32 text-[var(--brand-gold)]/10 rotate-12" />
                <div className="relative">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <div className="flex items-center gap-1.5 text-[var(--brand-gold)]">
                      {["s1","s2","s3","s4","s5"].map((id) => <Star key={id} className="w-5 h-5 fill-[var(--brand-gold)]" />)}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full bg-[var(--brand-teal)] text-white">
                      {featured.tag}
                    </span>
                  </div>
                  <p className="text-slate-800 text-lg md:text-xl leading-relaxed font-medium" style={{ fontFamily: "Outfit", fontWeight: 500 }}>
                    "{featured.text}"
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#e6e0d4] flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-extrabold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>{featured.name}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Verified review on {featured.source}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--brand-gold-deep)] flex items-center gap-1.5">
                      <BadgeCheck className="w-4 h-4" /> Featured Review
                    </span>
                  </div>
                </div>
              </article>
            );
          })()}

          {/* Other testimonials in a 2x2 grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.filter((t) => !t.featured).map((t) => (
              <article
                key={t.name}
                className="card-elevated p-7 flex flex-col"
                data-testid={`testimonial-${t.name.replace(/\W+/g, "-")}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1 text-[var(--brand-gold)]">
                    {["s1","s2","s3","s4","s5"].map((id) => <Star key={id} className="w-4 h-4 fill-[var(--brand-gold)]" />)}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)]">
                    {t.tag}
                  </span>
                </div>
                <p className="text-slate-700 leading-relaxed text-[15px] flex-1">"{t.text}"</p>
                <div className="mt-5 pt-4 border-t border-[#e6e0d4] flex items-center justify-between">
                  <div className="font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>{t.name}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1">
                    <BadgeCheck className="w-3 h-3 text-[var(--brand-gold)]" /> {t.source}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Verified Badge + Thumbtack */}
          <div className="mt-14 flex flex-wrap justify-center items-center gap-5" data-testid="testimonials-verified-badge">
            <div className="inline-flex items-center gap-3 bg-white border-2 border-[var(--brand-gold)] rounded-2xl px-6 py-4 shadow-md">
              <div className="flex -space-x-1">
                {["g1","g2","g3","g4","g5"].map((id) => <Star key={id} className="w-5 h-5 text-[var(--brand-gold)] fill-[var(--brand-gold)]" />)}
              </div>
              <div className="w-px h-8 bg-[#dcd5c6]" />
              <div className="text-left">
                <div className="font-extrabold text-[var(--brand-teal)] text-sm flex items-center gap-1.5" style={{ fontFamily: "Outfit" }}>
                  <BadgeCheck className="w-4 h-4 text-[var(--brand-gold)]" />
                  Verified 5.0 Star Professional
                </div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">on Thumbtack & Google</div>
              </div>
            </div>
            <ThumbtackBadge variant="featured" />
          </div>
        </div>
      </section>

      {/* ============== TRUST BAR ============== */}
      <section className="bg-white border-y border-[#e6e0d4] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-6 text-center items-center">
          {[
            { icon: Lock,        label: "256-Bit SSL Encryption" },
            { icon: BadgeCheck,  label: `Secure ${PROVIDER_LABEL} Checkout` },
            { icon: ShieldCheck, label: "Privacy Guaranteed" },
            { icon: CheckCircle2,label: "5-Star Reputation" },
          ].map((b) => (
            <div key={b.label} className="flex flex-col items-center gap-2">
              <b.icon className="w-7 h-7 text-[var(--brand-gold)]" />
              <span className="text-xs font-semibold text-[var(--brand-teal)] uppercase tracking-wider">{b.label}</span>
            </div>
          ))}
          <div className="flex justify-center">
            <ThumbtackBadge variant="compact" />
          </div>
        </div>
      </section>

      {/* ============== SERVICE AREA (SEO) ============== */}
      <section className="bg-white border-y border-[#e6e0d4] py-16" data-testid="service-area-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <div className="aspect-square rounded-2xl bg-[var(--brand-teal)] flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-6 rounded-full border-2 border-dashed border-[var(--brand-gold)]/60" />
              <div className="absolute inset-14 rounded-full border-2 border-dashed border-[var(--brand-gold)]/40" />
              <div className="relative text-center text-white">
                <MapPin className="w-10 h-10 text-[var(--brand-gold)] mx-auto mb-2" />
                <div className="text-3xl font-extrabold" style={{ fontFamily: "Outfit" }}>20-Mile</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--brand-gold)] font-bold mt-1">Instant Booking</div>
                <div className="mt-2 text-sm text-white/80">Evansville, IN</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="section-eyebrow">Local Coverage</span>
            <h2 className="heading-display mt-3 text-3xl md:text-4xl">Proudly Serving Evansville & The Tri-State Area</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              <strong>Instant Online Booking</strong> is a streamlined, automated way to schedule house cleaning within <strong>20 miles of Evansville</strong> — but it's just one of the many ways we serve our community. For <strong>handyman services</strong>, larger projects, or any cleaning request beyond that radius, we'd be happy to provide a custom, personalized quote. We're eager to help wherever you are in the Tri-State — simply{" "}
              <Link to="/request-quote" className="text-[var(--brand-teal)] font-semibold underline">request a quote</Link> or give us a call at <a href={BRAND.phoneHref} className="text-[var(--brand-teal)] font-semibold underline">{BRAND.phone}</a>.
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
              {["Evansville, IN", "Newburgh, IN", "Henderson, KY", "Boonville, IN", "Chandler, IN", "Mount Vernon, IN", "Owensboro outskirts", "Tri-State Area"].map((c) => (
                <li key={c} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--brand-gold)]" /> {c}</li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-slate-500 italic">
              No matter where you are in the Tri-State, we'd love to help. <Link to="/request-quote" className="text-[var(--brand-teal)] font-semibold underline">Request a personalized quote</Link> and we'll be in touch within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-[var(--brand-teal-deep)] p-10 md:p-16 text-white">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[var(--brand-gold)]/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Ready For Effortless Home Care?
              </h2>
              <p className="mt-3 text-white/80 max-w-lg">
                Get a real, transparent quote in under 60 seconds. No back-and-forth, no surprises.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/quote" className="btn-accent" data-testid="final-cta-quote">
                Get Instant Booking <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={BRAND.phoneHref} className="btn-outline border-white text-white hover:bg-white hover:text-[var(--brand-teal)]" data-testid="final-cta-call">
                <Phone className="w-4 h-4" /> Call {BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
